import type {
  CatalogService,
  CatalogServiceRevision,
  CommunicationMessageSummary,
  DispatchMetrics,
  OperationsActiveJob,
  OperationsDispatchState,
  OperationsOrder,
  PartnerApplicationSummary,
  QualityJobSummary,
  ReadinessResponse,
  ServiceCatalog,
  SupportCaseSummary,
  SafetyIncidentSummary,
  FinanceOperationsQueue,
  FinanceOwnerSummary,
  IdentityRole,
  OperationsAuditEvent,
  OperationsUserSummary,
  OperationsUsersPage,
  RetentionRunSummary,
} from "@cleany/platform-contracts";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import {
  loadDispatchMetrics,
  loadOperationsActiveJobs,
  loadOperationsCatalog,
  loadOperationsDispatch,
  loadOperationsOrders,
  loadOperationsQualityJobs,
  loadOperationsSupportCase,
  loadOperationsSupportCases,
  loadPartnerApplications,
  manuallyAssignOrder,
  publishServiceRevision,
  requestOperationsOtp,
  joinOperationsSupportCase,
  resolveOperationsActiveJobAlert,
  resolveOperationsQualityClaim,
  resolveOperationsSupportCase,
  restartOperationsDispatch,
  sendOperationsSupportMessage,
  reviewPartnerApplication,
  setPartnerLevel,
  verifyOperationsOtp,
  assignSafetyIncident,
  closeSafetyIncident,
  completeSafetyPlaybookStep,
  loadSafetyIncidents,
  mitigateSafetyIncident,
  requestStaffStepUp,
  restrictSafetySubject,
  triageSafetyIncident,
  verifyStaffStepUp,
  viewSensitiveSafetyIncident,
  loadOperationsFinanceQueue,
  loadFinanceOwnerSummary,
  createOperationsRefund,
  processOperationsPayout,
  scheduleOperationsPayout,
  clearOperationsSession,
  restoreOperationsSession,
  saveOperationsSession,
  loadOperationsUsers,
  setOperationsUserStatus,
  setOperationsStaffRole,
  loadOperationsAudit,
  loadRetentionRuns,
  runRetention,
} from "./api.js";
import { type OperationsLocale, operationsText } from "./i18n.js";
import { readinessState } from "./status.js";
import { MarketplaceCenter } from "./MarketplaceCenter.js";
import { OperationsIntegrationsCenter } from "./IntegrationsCenter.js";

type OperationsWorkspace =
  | "overview"
  | "orders"
  | "active"
  | "quality"
  | "support"
  | "safety"
  | "users"
  | "partners"
  | "finance"
  | "catalog"
  | "governance"
  | "integrations";

const workspaceDefinitions: Readonly<
  Record<
    OperationsWorkspace,
    { readonly code: string; readonly title: string; readonly subtitle: string }
  >
> = {
  overview: {
    code: "01",
    title: "Заявки и предложения",
    subtitle: "Публикации клиентов, предложения клинеров и модерация",
  },
  orders: {
    code: "02",
    title: "Заказы",
    subtitle: "Вся цепочка заказа, назначение партнёра и история статусов",
  },
  active: {
    code: "03",
    title: "Активные уборки",
    subtitle: "Контроль прибытия, выполнения и отклонений от SLA",
  },
  quality: {
    code: "04",
    title: "Качество",
    subtitle: "Приёмка, претензии, исправления и доказательства выполнения",
  },
  support: {
    code: "05",
    title: "Поддержка",
    subtitle: "Диалоги клиента и партнёра, подключение оператора и решения",
  },
  safety: {
    code: "06",
    title: "Безопасность",
    subtitle: "Инциденты, playbook, ограничения и защищённые действия",
  },
  users: {
    code: "07",
    title: "Пользователи и команда",
    subtitle: "Клиенты, партнёры, сотрудники, доступы и состояние аккаунтов",
  },
  partners: {
    code: "08",
    title: "Допуск партнёров",
    subtitle: "Анкеты, документы, обучение, контрольное задание и уровни",
  },
  finance: {
    code: "09",
    title: "Финансы",
    subtitle: "Проводки, возвраты, выплаты, сверка и экономика сервиса",
  },
  catalog: {
    code: "10",
    title: "Каталог и тарифы",
    subtitle: "Услуги, зоны, длительность и версионируемые правила цены",
  },
  governance: {
    code: "11",
    title: "Доступ и аудит",
    subtitle: "Неизменяемый журнал действий, роли и политика хранения данных",
  },
  integrations: {
    code: "12",
    title: "Каналы и API",
    subtitle: "OTP, SMS, WhatsApp, Telegram, ключи провайдеров и диагностика доставки",
  },
};

const workspaceGroups: readonly {
  readonly label: string;
  readonly items: readonly OperationsWorkspace[];
}[] = [
  { label: "Площадка", items: ["overview", "users"] },
  { label: "Настройки", items: ["catalog", "governance", "integrations"] },
];

export function App() {
  const [locale, setLocale] = useState<OperationsLocale>("ru");
  const [readiness, setReadiness] = useState<ReadinessResponse | null>(null);
  const [token, setToken] = useState(() => restoreOperationsSession()?.accessToken ?? "");
  const [workspace, setWorkspace] = useState<OperationsWorkspace>(() => workspaceFromHash());
  const [globalSearch, setGlobalSearch] = useState("");
  const [directoryQuery, setDirectoryQuery] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/health/ready", { signal: controller.signal })
      .then((response) => response.json() as Promise<ReadinessResponse>)
      .then(setReadiness)
      .catch(() =>
        setReadiness({
          checks: { api: "failed" },
          status: "degraded",
          timestamp: new Date().toISOString(),
        }),
      );
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const onHashChange = () => setWorkspace(workspaceFromHash());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    const update = (event: Event) => {
      const accessToken = (event as CustomEvent<string>).detail;
      if (accessToken) setToken(accessToken);
    };
    window.addEventListener("cleany:operations-session", update);
    return () => window.removeEventListener("cleany:operations-session", update);
  }, []);

  const text = (key: Parameters<typeof operationsText>[1]) => operationsText(locale, key);
  const state = readinessState(readiness);
  const session = restoreOperationsSession();
  const profile = session?.accessToken === token ? session.profile : null;
  const definition = workspaceDefinitions[workspace];

  function selectWorkspace(next: OperationsWorkspace) {
    setWorkspace(next);
    window.history.replaceState(null, "", `#${next}`);
    window.scrollTo({ behavior: "smooth", top: 0 });
  }

  if (!token) {
    return (
      <div className="operationsLoginShell">
        <aside className="loginStory">
          <div className="brandLogo brandLogo--large" aria-label="Cleany" role="img">
            <span className="brandSymbol" aria-hidden="true">
              C<span>✦</span>
            </span>
            <strong>cleany</strong>
          </div>
          <p className="eyebrow">Operations OS</p>
          <h1>Заявки, предложения и люди.</h1>
          <p>Управляйте публикациями, пользователями и тарифами бесплатной площадки уборки.</p>
          <div className={`loginApiStatus status--${state}`}>
            <span aria-hidden="true" />
            {state === "ready" ? "Платформа доступна" : "Проверяем платформу"}
          </div>
        </aside>
        <OperationsSignIn
          onSignedIn={(nextSession) => {
            saveOperationsSession(nextSession);
            setToken(nextSession.accessToken);
          }}
        />
      </div>
    );
  }

  return (
    <div className="adminShell">
      <aside className="adminSidebar" aria-label={text("brandSubtitle")}>
        <header className="brand">
          <div className="brandLogo" aria-label="Cleany" role="img">
            <span className="brandSymbol" aria-hidden="true">
              C<span>✦</span>
            </span>
            <strong>cleany</strong>
          </div>
          <span>{text("brandSubtitle")}</span>
        </header>
        <div className="sidebarLiveBadge">
          <span aria-hidden="true" />
          Площадка онлайн
        </div>
        <nav className="adminNavigation" aria-label={text("dashboard")}>
          {workspaceGroups.map((group) => (
            <section className="navGroup" key={group.label}>
              <p>{group.label}</p>
              {group.items.map((item) => {
                const candidate = workspaceDefinitions[item];
                return (
                  <button
                    aria-current={workspace === item ? "page" : undefined}
                    className={
                      workspace === item ? "adminNavItem adminNavItem--active" : "adminNavItem"
                    }
                    key={item}
                    onClick={() => selectWorkspace(item)}
                    type="button"
                  >
                    <span>{candidate.code}</span>
                    {candidate.title}
                  </button>
                );
              })}
            </section>
          ))}
        </nav>
        <div className="operatorCard">
          <span className="operatorAvatar">{initials(profile?.displayName ?? "Operations")}</span>
          <div>
            <strong>{profile?.displayName ?? "Operations"}</strong>
            <small>{profile?.roles.map(roleLabel).join(" · ")}</small>
          </div>
        </div>
      </aside>
      <div className="adminWorkspace">
        <header className="adminTopbar">
          <div className="workspaceIdentity">
            <span>{definition.code}</span>
            <div>
              <strong>{definition.title}</strong>
              <small>{definition.subtitle}</small>
            </div>
          </div>
          <form
            className="globalSearch"
            onSubmit={(event) => {
              event.preventDefault();
              const query = globalSearch.trim();
              if (query.length < 2) return;
              setDirectoryQuery(query);
              selectWorkspace("users");
            }}
          >
            <span aria-hidden="true">⌕</span>
            <input
              aria-label="Глобальный поиск пользователя"
              onChange={(event) => setGlobalSearch(event.target.value)}
              placeholder="Имя, телефон или ID"
              value={globalSearch}
            />
            <kbd>Enter</kbd>
          </form>
          <div className="topbarActions">
            <div className={`apiPulse status--${state}`} title="Состояние API">
              <span aria-hidden="true" />
              {state === "ready" ? "API" : "…"}
            </div>
            <fieldset className="localeSwitch">
              <legend className="visuallyHidden">Language</legend>
              {(["ru", "kk"] as const).map((candidate) => (
                <button
                  aria-pressed={locale === candidate}
                  className={
                    locale === candidate ? "localeButton localeButton--active" : "localeButton"
                  }
                  key={candidate}
                  onClick={() => setLocale(candidate)}
                  type="button"
                >
                  {candidate.toUpperCase()}
                </button>
              ))}
            </fieldset>
            <button
              className="signOutButton"
              type="button"
              onClick={() => {
                clearOperationsSession();
                setToken("");
              }}
            >
              Выйти
            </button>
          </div>
        </header>
        <main className="adminContent">
          <OperationsWorkspaceContent
            currentRoles={profile?.roles ?? []}
            directoryQuery={directoryQuery}
            locale={locale}
            onUnauthorized={() => {
              clearOperationsSession();
              setToken("");
            }}
            token={token}
            workspace={workspace}
          />
        </main>
      </div>
    </div>
  );
}

function OperationsWorkspaceContent({
  workspace,
  token,
  locale,
  directoryQuery,
  currentRoles,
  onUnauthorized,
}: {
  readonly workspace: OperationsWorkspace;
  readonly token: string;
  readonly locale: OperationsLocale;
  readonly directoryQuery: string;
  readonly currentRoles: readonly IdentityRole[];
  readonly onUnauthorized: () => void;
}) {
  switch (workspace) {
    case "overview":
      return <MarketplaceCenter token={token} />;
    case "orders":
      return <OperationsOrderQueue token={token} />;
    case "active":
      return <OperationsActiveJobCenter token={token} />;
    case "quality":
      return <OperationsQualityCenter token={token} />;
    case "support":
      return <OperationsSupportCenter token={token} />;
    case "safety":
      return <OperationsSafetyCenter token={token} />;
    case "users":
      return (
        <OperationsUsersCenter
          currentRoles={currentRoles}
          initialQuery={directoryQuery}
          token={token}
        />
      );
    case "partners":
      return <PartnerApplicationsReview token={token} />;
    case "finance":
      return <OperationsFinanceCenter token={token} />;
    case "catalog":
      return <CatalogWorkspace token={token} locale={locale} onUnauthorized={onUnauthorized} />;
    case "governance":
      return <OperationsGovernanceCenter currentRoles={currentRoles} token={token} />;
    case "integrations":
      return <OperationsIntegrationsCenter currentRoles={currentRoles} token={token} />;
  }
}

function workspaceFromHash(): OperationsWorkspace {
  const candidate = window.location.hash.replace(/^#/, "") as OperationsWorkspace;
  return workspaceGroups.some((group) => group.items.includes(candidate)) ? candidate : "overview";
}

function initials(value: string): string {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toLocaleUpperCase("ru"))
    .join("");
}

function roleLabel(role: IdentityRole): string {
  return {
    customer: "Клиент",
    operator: "Оператор",
    owner: "Владелец",
    partner: "Партнёр",
    partner_candidate: "Кандидат",
    supervisor: "Супервайзер",
  }[role];
}

function OperationsUsersCenter({
  token,
  initialQuery,
  currentRoles,
}: {
  readonly token: string;
  readonly initialQuery: string;
  readonly currentRoles: readonly IdentityRole[];
}) {
  const [query, setQuery] = useState(initialQuery);
  const [role, setRole] = useState<IdentityRole | "">("");
  const [status, setStatus] = useState<OperationsUserSummary["status"] | "">("");
  const [page, setPage] = useState<OperationsUsersPage | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (initialQuery) setQuery(initialQuery);
  }, [initialQuery]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const normalizedQuery = query.trim();
      const result = await loadOperationsUsers(token, {
        limit: 50,
        ...(normalizedQuery.length >= 2 ? { query: normalizedQuery } : {}),
        ...(role ? { role } : {}),
        ...(status ? { status } : {}),
      });
      setPage(result);
      setSelectedId((current) => (result.users.some((user) => user.id === current) ? current : ""));
      setError("");
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Каталог пользователей недоступен");
    } finally {
      setLoading(false);
    }
  }, [query, role, status, token]);

  useEffect(() => {
    const timer = window.setTimeout(() => void reload(), 250);
    return () => window.clearTimeout(timer);
  }, [reload]);

  const selected = page?.users.find((user) => user.id === selectedId) ?? null;
  const blocked = page?.users.filter((user) => user.status === "blocked").length ?? 0;
  const staff =
    page?.users.filter((user) =>
      user.roles.some((candidate) => ["operator", "supervisor", "owner"].includes(candidate)),
    ).length ?? 0;

  return (
    <section className="peopleCenter" aria-labelledby="people-title">
      <div className="workspaceHeader adminSectionHeader">
        <div>
          <p className="eyebrow">Identity directory</p>
          <h1 id="people-title">Пользователи и команда</h1>
          <p>Поиск, состояние аккаунта и роли сотрудников без раскрытия полного телефона.</p>
        </div>
        <div className="directoryMetrics">
          <div>
            <span>Найдено</span>
            <strong>{page?.total ?? "—"}</strong>
          </div>
          <div>
            <span>Заблокировано</span>
            <strong>{blocked}</strong>
          </div>
          <div>
            <span>Команда</span>
            <strong>{staff}</strong>
          </div>
        </div>
      </div>

      <form
        className="directoryToolbar panel"
        onSubmit={(event) => {
          event.preventDefault();
          void reload();
        }}
      >
        <label className="directorySearchField">
          <span>Поиск</span>
          <input
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Имя, маска телефона или UUID"
            value={query}
          />
        </label>
        <label>
          <span>Роль</span>
          <select
            onChange={(event) => setRole(event.target.value as IdentityRole | "")}
            value={role}
          >
            <option value="">Все роли</option>
            <option value="customer">Клиент</option>
            <option value="partner_candidate">Кандидат</option>
            <option value="partner">Партнёр</option>
            <option value="operator">Оператор</option>
            <option value="supervisor">Супервайзер</option>
            <option value="owner">Владелец</option>
          </select>
        </label>
        <label>
          <span>Состояние</span>
          <select
            onChange={(event) =>
              setStatus(event.target.value as OperationsUserSummary["status"] | "")
            }
            value={status}
          >
            <option value="">Все состояния</option>
            <option value="active">Активен</option>
            <option value="blocked">Заблокирован</option>
            <option value="deletion_pending">Ожидает удаления</option>
          </select>
        </label>
        <button className="primaryButton" type="submit">
          Найти
        </button>
      </form>

      {error && <div className="notice notice--error">{error}</div>}
      {loading && <div className="notice">Обновляем каталог пользователей…</div>}
      {!loading && !error && page?.users.length === 0 && (
        <div className="notice">По выбранным условиям никого не найдено.</div>
      )}

      {page && page.users.length > 0 && (
        <div className={selected ? "directoryLayout directoryLayout--selected" : "directoryLayout"}>
          <div className="directoryTable panel">
            <div className="directoryTableHead">
              <span>Пользователь</span>
              <span>Роли</span>
              <span>Активность</span>
              <span>Статус</span>
            </div>
            {page.users.map((user) => (
              <button
                className={
                  selectedId === user.id ? "directoryRow directoryRow--active" : "directoryRow"
                }
                key={user.id}
                onClick={() => setSelectedId(user.id)}
                type="button"
              >
                <span className="directoryIdentity">
                  <span className="userMonogram">{initials(user.displayName ?? "C")}</span>
                  <span>
                    <strong>{user.displayName ?? "Без имени"}</strong>
                    <small>
                      {user.phoneMasked} · {user.id.slice(0, 8)}
                    </small>
                  </span>
                </span>
                <span className="roleStack">
                  {user.roles.map((candidate) => (
                    <small key={candidate}>{roleLabel(candidate)}</small>
                  ))}
                </span>
                <span>{user.lastSeenAt ? formatDateTime(user.lastSeenAt) : "Не входил"}</span>
                <span>
                  <span className={`accountStatus accountStatus--${user.status}`}>
                    {identityStatusLabel(user.status)}
                  </span>
                </span>
              </button>
            ))}
          </div>
          {selected && (
            <OperationsUserControl
              currentRoles={currentRoles}
              key={`${selected.id}:${selected.status}:${selected.roles.join(",")}`}
              onChanged={reload}
              onClose={() => setSelectedId("")}
              token={token}
              user={selected}
            />
          )}
        </div>
      )}
    </section>
  );
}

type PendingIdentityAction =
  | { readonly kind: "status"; readonly status: "active" | "blocked" }
  | { readonly kind: "role"; readonly role: "operator" | "supervisor" | "owner" };

function OperationsUserControl({
  user,
  token,
  currentRoles,
  onChanged,
  onClose,
}: {
  readonly user: OperationsUserSummary;
  readonly token: string;
  readonly currentRoles: readonly IdentityRole[];
  readonly onChanged: () => Promise<void>;
  readonly onClose: () => void;
}) {
  const currentStaffRole = user.roles.find((role) =>
    ["operator", "supervisor", "owner"].includes(role),
  ) as "operator" | "supervisor" | "owner" | undefined;
  const [role, setRole] = useState<"operator" | "supervisor" | "owner">(
    currentStaffRole ?? "operator",
  );
  const [reason, setReason] = useState("Решение принято после проверки профиля и истории");
  const [pending, setPending] = useState<PendingIdentityAction | null>(null);
  const [challengeId, setChallengeId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const canManageStatus = currentRoles.some((candidate) =>
    ["supervisor", "owner"].includes(candidate),
  );
  const canManageRoles = currentRoles.includes("owner");

  async function begin(action: PendingIdentityAction) {
    setBusy(true);
    setNotice("");
    try {
      const challenge = await requestStaffStepUp(
        token,
        action.kind === "status" ? "identity.user_status_change" : "staff.role_change",
        user.id,
      );
      setPending(action);
      setChallengeId(challenge.challengeId);
      setNotice("Одноразовый код отправлен сотруднику. Введите его для подтверждения.");
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Защищённое действие недоступно");
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!pending) return;
    setBusy(true);
    try {
      const grant = await verifyStaffStepUp(token, challengeId, code);
      if (pending.kind === "status") {
        await setOperationsUserStatus(token, user.id, pending.status, reason, grant.stepUpToken);
      } else {
        await setOperationsStaffRole(token, user.id, pending.role, grant.stepUpToken);
      }
      setPending(null);
      setChallengeId("");
      setCode("");
      setNotice("Изменение сохранено и записано в аудит.");
      await onChanged();
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Изменение не выполнено");
    } finally {
      setBusy(false);
    }
  }

  return (
    <aside className="userControl panel" aria-label={`Управление ${user.displayName ?? user.id}`}>
      <div className="userControlHeader">
        <div>
          <p className="cardLabel">Карточка пользователя</p>
          <h2>{user.displayName ?? "Без имени"}</h2>
        </div>
        <button
          aria-label="Закрыть карточку"
          className="iconButton"
          onClick={onClose}
          type="button"
        >
          ×
        </button>
      </div>
      <dl className="userFacts">
        <div>
          <dt>ID</dt>
          <dd>
            <code>{user.id}</code>
          </dd>
        </div>
        <div>
          <dt>Телефон</dt>
          <dd>{user.phoneMasked}</dd>
        </div>
        <div>
          <dt>Создан</dt>
          <dd>{formatDateTime(user.createdAt)}</dd>
        </div>
        <div>
          <dt>Партнёр</dt>
          <dd>{admissionLabel(user.partnerAdmission)}</dd>
        </div>
      </dl>
      <label className="reviewField">
        Причина изменения
        <textarea
          minLength={8}
          onChange={(event) => setReason(event.target.value)}
          rows={3}
          value={reason}
        />
      </label>

      {canManageStatus && (
        <div className="controlBlock">
          <div>
            <strong>Состояние аккаунта</strong>
            <small>Блокировка немедленно завершит активные сессии.</small>
          </div>
          <button
            className={user.status === "blocked" ? "quietButton" : "dangerButton"}
            disabled={busy || reason.trim().length < 8}
            onClick={() =>
              void begin({
                kind: "status",
                status: user.status === "blocked" ? "active" : "blocked",
              })
            }
            type="button"
          >
            {user.status === "blocked" ? "Разблокировать" : "Заблокировать"}
          </button>
        </div>
      )}

      {canManageRoles && (
        <div className="controlBlock controlBlock--stacked">
          <div>
            <strong>Роль сотрудника</strong>
            <small>Изменение роли требует отдельного одноразового подтверждения.</small>
          </div>
          <div className="roleControl">
            <select
              onChange={(event) =>
                setRole(event.target.value as "operator" | "supervisor" | "owner")
              }
              value={role}
            >
              <option value="operator">Оператор</option>
              <option value="supervisor">Супервайзер</option>
              <option value="owner">Владелец</option>
            </select>
            <button
              className="quietButton"
              disabled={busy || reason.trim().length < 8 || role === currentStaffRole}
              onClick={() => void begin({ kind: "role", role })}
              type="button"
            >
              Изменить роль
            </button>
          </div>
        </div>
      )}

      {pending && (
        <div className="stepUpCard">
          <label>
            Код подтверждения
            <input
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
              value={code}
            />
          </label>
          <button disabled={busy || code.length !== 6} onClick={() => void confirm()} type="button">
            Подтвердить действие
          </button>
        </div>
      )}
      {notice && <p className="formNotice">{notice}</p>}
    </aside>
  );
}

function OperationsGovernanceCenter({
  token,
  currentRoles,
}: {
  readonly token: string;
  readonly currentRoles: readonly IdentityRole[];
}) {
  const [events, setEvents] = useState<readonly OperationsAuditEvent[]>([]);
  const [runs, setRuns] = useState<readonly RetentionRunSummary[]>([]);
  const [filter, setFilter] = useState("");
  const [error, setError] = useState("");
  const canRunRetention = currentRoles.includes("owner");

  const reload = useCallback(async () => {
    try {
      const [nextEvents, nextRuns] = await Promise.all([
        loadOperationsAudit(token),
        canRunRetention ? loadRetentionRuns(token) : Promise.resolve([]),
      ]);
      setEvents(nextEvents);
      setRuns(nextRuns);
      setError("");
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Контрольный журнал недоступен");
    }
  }, [canRunRetention, token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const normalizedFilter = filter.trim().toLocaleLowerCase("ru");
  const visibleEvents = events.filter((event) =>
    [event.eventType, event.subjectType, event.subjectId, event.actorUserId ?? "system"].some(
      (value) => value.toLocaleLowerCase("ru").includes(normalizedFilter),
    ),
  );

  return (
    <section className="governanceCenter" aria-labelledby="governance-title">
      <div className="workspaceHeader adminSectionHeader">
        <div>
          <p className="eyebrow">Governance &amp; privacy</p>
          <h1 id="governance-title">Доступ и аудит</h1>
          <p>Кто, когда и почему изменил заказ, профиль, деньги или настройки платформы.</p>
        </div>
        <button className="quietButton" onClick={() => void reload()} type="button">
          Обновить
        </button>
      </div>
      {error && <div className="notice notice--error">{error}</div>}
      <div className="governanceGrid">
        <article className="panel auditWorkspace">
          <div className="panelHeadingRow">
            <div>
              <p className="cardLabel">Append-only</p>
              <h2>Журнал действий</h2>
            </div>
            <input
              aria-label="Фильтр журнала"
              onChange={(event) => setFilter(event.target.value)}
              placeholder="Событие, субъект или ID"
              value={filter}
            />
          </div>
          <div className="auditTable">
            {visibleEvents.slice(0, 100).map((event) => (
              <div
                className="auditRow"
                key={`${event.createdAt}:${event.eventType}:${event.subjectId}`}
              >
                <span className="auditEventMark" aria-hidden="true" />
                <div>
                  <strong>{auditEventLabel(event.eventType)}</strong>
                  <small>
                    {event.subjectType} · {event.subjectId}
                  </small>
                </div>
                <div>
                  <span>{event.actorUserId ? event.actorUserId.slice(0, 8) : "system"}</span>
                  <small>{formatDateTime(event.createdAt)}</small>
                </div>
              </div>
            ))}
            {visibleEvents.length === 0 && <p>Событий по фильтру нет.</p>}
          </div>
        </article>

        <article className="panel retentionWorkspace">
          <p className="cardLabel">Privacy operations</p>
          <h2>Хранение и удаление</h2>
          <p>
            Сначала безопасная оценка, затем отдельная постановка допустимых объектов в очередь
            удаления. Legal hold всегда имеет приоритет.
          </p>
          {canRunRetention ? (
            <RetentionControls token={token} onChanged={reload} />
          ) : (
            <div className="permissionNote">Запуск доступен только владельцу.</div>
          )}
          <div className="retentionRuns">
            {runs.slice(0, 8).map((run) => (
              <div key={run.id}>
                <span
                  className={`statusPill statusPill--${run.mode === "evaluate" ? "accepted" : "warning"}`}
                >
                  {run.mode === "evaluate" ? "Оценка" : "Очередь удаления"}
                </span>
                <strong>{run.eligibleCount} допустимо</strong>
                <small>
                  {run.heldCount} hold · {run.queuedCount} в очереди
                </small>
              </div>
            ))}
            {runs.length === 0 && canRunRetention && <p>Запусков ещё не было.</p>}
          </div>
        </article>
      </div>
    </section>
  );
}

function RetentionControls({
  token,
  onChanged,
}: {
  readonly token: string;
  readonly onChanged: () => Promise<void>;
}) {
  const [mode, setMode] = useState<"evaluate" | "queue_deletion">("evaluate");
  const [challengeId, setChallengeId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  async function begin() {
    setBusy(true);
    try {
      const challenge = await requestStaffStepUp(
        token,
        "retention.run",
        "00000000-0000-4000-8000-000000001011",
      );
      setChallengeId(challenge.challengeId);
      setNotice("Введите одноразовый код владельца.");
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Проверка недоступна");
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    setBusy(true);
    try {
      const grant = await verifyStaffStepUp(token, challengeId, code);
      await runRetention(token, grant.stepUpToken, mode);
      setChallengeId("");
      setCode("");
      setNotice("Запуск завершён и записан в аудит.");
      await onChanged();
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Запуск не выполнен");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="retentionControls">
      <select
        onChange={(event) => setMode(event.target.value as "evaluate" | "queue_deletion")}
        value={mode}
      >
        <option value="evaluate">Только оценить</option>
        <option value="queue_deletion">Поставить в очередь удаления</option>
      </select>
      {!challengeId ? (
        <button
          className={mode === "evaluate" ? "quietButton" : "dangerButton"}
          disabled={busy}
          onClick={() => void begin()}
          type="button"
        >
          {mode === "evaluate" ? "Запустить оценку" : "Подтвердить удаление"}
        </button>
      ) : (
        <div className="stepUpInline">
          <input
            aria-label="Код владельца"
            inputMode="numeric"
            maxLength={6}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
            value={code}
          />
          <button disabled={busy || code.length !== 6} onClick={() => void confirm()} type="button">
            Выполнить
          </button>
        </div>
      )}
      {notice && <small>{notice}</small>}
    </div>
  );
}

function identityStatusLabel(status: OperationsUserSummary["status"]): string {
  return {
    active: "Активен",
    blocked: "Заблокирован",
    deletion_pending: "Ожидает удаления",
  }[status];
}

function admissionLabel(status: OperationsUserSummary["partnerAdmission"]): string {
  return {
    admitted: "Допущен",
    not_submitted: "Не подавал заявку",
    pending: "На проверке",
    rejected: "Отклонён",
  }[status];
}

function auditEventLabel(eventType: string): string {
  const labels: Readonly<Record<string, string>> = {
    "operations.user_status_changed": "Изменено состояние пользователя",
    "operations.staff_role_changed": "Изменена роль сотрудника",
    "operations.manual_action_executed": "Выполнено ручное действие",
    "operations.feature_flag_changed": "Изменена функция платформы",
    "finance.refund_created": "Создан возврат",
    "integration.otp_policy_updated": "Изменена политика OTP",
    "integration.provider_test_succeeded": "Провайдер успешно проверен",
    "integration.provider_updated": "Обновлена интеграция",
    "partner.availability_updated": "Партнёр изменил график",
    "partner.time_off_added": "Партнёр добавил перерыв",
    "partner.time_off_deleted": "Партнёр удалил перерыв",
    "partner.work_status_updated": "Партнёр изменил доступность",
    "privacy.retention_run_completed": "Завершена политика хранения",
  };
  return labels[eventType] ?? eventType.replaceAll("_", " ");
}

function OperationsFinanceCenter({ token }: { readonly token: string }) {
  const [queue, setQueue] = useState<FinanceOperationsQueue | null>(null);
  const [ownerSummary, setOwnerSummary] = useState<FinanceOwnerSummary | null>(null);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    try {
      const financeQueue = await loadOperationsFinanceQueue(token);
      setQueue(financeQueue);
      setError("");
      try {
        setOwnerSummary(await loadFinanceOwnerSummary(token));
      } catch {
        setOwnerSummary(null);
      }
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Финансовая очередь недоступна");
    }
  }, [token]);

  useEffect(() => {
    void reload();
    const timer = window.setInterval(() => void reload(), 20_000);
    return () => window.clearInterval(timer);
  }, [reload]);

  const captures = queue?.transactions.filter((item) => item.type === "capture") ?? [];
  const refunds = queue?.transactions.filter((item) => item.type === "refund") ?? [];
  const partnerIds = [...new Set(captures.flatMap((item) => item.partnerUserId ?? []))];
  const refundableCaptures = captures
    .flatMap((capture) => {
      const orderId = capture.orderId;
      if (!orderId) return [];
      return [
        {
          capture,
          orderId,
          availableMinor: Math.max(
            0,
            capture.amountMinor -
              refunds
                .filter((refund) => refund.orderId === orderId)
                .reduce((total, refund) => total + refund.amountMinor, 0),
          ),
        },
      ];
    })
    .filter((item) => item.availableMinor > 0);

  return (
    <section className="financeCenter" id="finance" aria-labelledby="finance-title">
      <div className="workspaceHeader">
        <div>
          <p className="eyebrow">Итерация 12 · Ledger &amp; Payouts</p>
          <h1 id="finance-title">Деньги и сверка</h1>
          <p>
            Capture, возвраты и выплаты публикуются только сбалансированными проводками. Сумма
            выплаты выводится из partner payable, а не из изменяемого заказа.
          </p>
        </div>
        <div className="dispatchHeaderActions">
          <div className="dispatchMetric">
            <span>Capture</span>
            <strong>{captures.length}</strong>
          </div>
          <div className="dispatchMetric">
            <span>Возвраты</span>
            <strong>{refunds.length}</strong>
          </div>
          <div
            className={
              (queue?.reconciliationCases.length ?? 0) > 0
                ? "dispatchMetric dispatchMetric--warning"
                : "dispatchMetric"
            }
          >
            <span>Несверено</span>
            <strong>{queue?.reconciliationCases.length ?? 0}</strong>
          </div>
          <button className="quietButton" onClick={() => void reload()} type="button">
            Обновить
          </button>
        </div>
      </div>

      {error && <div className="notice notice--error">{error}</div>}
      {!queue && !error && <div className="notice">Сверяем ledger и провайдера…</div>}

      {ownerSummary && (
        <section className="financeOwnerGrid" aria-label="Экономика собственника">
          <FinanceMetric label="GMV" value={formatMoney(ownerSummary.gmvMinor)} />
          <FinanceMetric label="Чистая выручка" value={formatMoney(ownerSummary.netRevenueMinor)} />
          <FinanceMetric
            label="Обязательства партнёрам"
            value={formatMoney(ownerSummary.partnerLiabilityMinor)}
          />
          <FinanceMetric
            label="Contribution margin"
            value={formatMoney(ownerSummary.contributionMarginMinor)}
          />
        </section>
      )}

      {queue && (
        <div className="financeColumns">
          <article className="panel financeLedgerPanel">
            <div className="queueCardHeader">
              <h2>Последние проводки</h2>
              <span className="statusPill statusPill--accepted">append-only</span>
            </div>
            {queue.transactions.slice(0, 12).map((transaction) => (
              <div className="financeRow" key={transaction.id}>
                <div>
                  <strong>{financeTypeLabel(transaction.type)}</strong>
                  <small>
                    {transaction.orderId
                      ? `заказ ${transaction.orderId.slice(0, 8)}`
                      : "без заказа"}
                  </small>
                </div>
                <strong>{formatMoney(transaction.amountMinor)}</strong>
                <span className={transaction.balanced ? "ledgerBalanced" : "ledgerUnbalanced"}>
                  {transaction.balanced ? "D = C" : "ошибка"}
                </span>
              </div>
            ))}
            {queue.transactions.length === 0 && <p>Принятых заказов пока нет.</p>}
          </article>

          <article className="panel financeLedgerPanel">
            <h2>Выплаты</h2>
            {queue.payouts.map((payout) => (
              <div className="financePayout" key={payout.id}>
                <div>
                  <strong>{formatMoney(payout.amountMinor)}</strong>
                  <small>
                    {payout.partnerUserId.slice(0, 8)} · {financePayoutStatus(payout.status)}
                  </small>
                </div>
                {payout.status === "scheduled" && (
                  <FinancePayoutAction
                    kind="process"
                    onChanged={reload}
                    payoutId={payout.id}
                    subjectId={payout.id}
                    token={token}
                  />
                )}
              </div>
            ))}
            {queue.payouts.length === 0 && <p>Активных выплат нет.</p>}
            {partnerIds.slice(0, 5).map((partnerUserId) => (
              <FinancePayoutAction
                key={partnerUserId}
                kind="schedule"
                onChanged={reload}
                partnerUserId={partnerUserId}
                subjectId={partnerUserId}
                token={token}
              />
            ))}
          </article>

          <article className="panel financeLedgerPanel financeRefundPanel">
            <div className="queueCardHeader">
              <h2>Возвраты клиентам</h2>
              <span className="statusPill">step-up</span>
            </div>
            {refundableCaptures.slice(0, 8).map(({ capture, orderId, availableMinor }) => (
              <FinanceRefundAction
                availableMinor={availableMinor}
                key={capture.id}
                onChanged={reload}
                orderId={orderId}
                token={token}
              />
            ))}
            {refundableCaptures.length === 0 && <p>Нет операций, доступных для возврата.</p>}
          </article>

          <article className="panel financeLedgerPanel">
            <h2>Сверка провайдера</h2>
            {queue.reconciliationCases.map((record) => (
              <div className="reconciliationCase" key={record.id}>
                <strong>{reconciliationLabel(record.issue)}</strong>
                <code>{record.providerReference}</code>
                <span>
                  ledger {record.ledgerAmountMinor ?? "—"} · provider{" "}
                  {record.providerAmountMinor ?? "—"}
                </span>
              </div>
            ))}
            {queue.reconciliationCases.length === 0 && (
              <p className="ledgerBalanced">Открытых расхождений нет.</p>
            )}
          </article>
        </div>
      )}
    </section>
  );
}

function FinanceRefundAction({
  token,
  orderId,
  availableMinor,
  onChanged,
}: {
  readonly token: string;
  readonly orderId: string;
  readonly availableMinor: number;
  readonly onChanged: () => Promise<void>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [amountTenge, setAmountTenge] = useState(Math.round(availableMinor / 100));
  const [reason, setReason] = useState("Возврат после подтверждённой претензии клиента");
  const [challengeId, setChallengeId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  async function begin() {
    setBusy(true);
    setNotice("");
    try {
      const challenge = await requestStaffStepUp(token, "finance.refund", orderId);
      setChallengeId(challenge.challengeId);
      setNotice("Введите одноразовый код для подтверждения движения денег.");
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Подтверждение недоступно");
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    setBusy(true);
    try {
      const grant = await verifyStaffStepUp(token, challengeId, code);
      await createOperationsRefund(token, orderId, amountTenge * 100, reason, grant.stepUpToken);
      setNotice("Возврат создан, проводки сбалансированы.");
      setChallengeId("");
      setCode("");
      setExpanded(false);
      await onChanged();
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Возврат не выполнен");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="refundAction">
      <button
        className="refundSummary"
        onClick={() => setExpanded((value) => !value)}
        type="button"
      >
        <span>
          <strong>Заказ {orderId.slice(0, 8)}</strong>
          <small>Доступно {formatMoney(availableMinor)}</small>
        </span>
        <span>{expanded ? "Свернуть" : "Оформить"}</span>
      </button>
      {expanded && (
        <div className="refundForm">
          <label>
            Сумма, ₸
            <input
              max={Math.floor(availableMinor / 100)}
              min={1}
              onChange={(event) => setAmountTenge(Number(event.target.value))}
              type="number"
              value={amountTenge}
            />
          </label>
          <label>
            Основание
            <textarea
              minLength={8}
              onChange={(event) => setReason(event.target.value)}
              rows={2}
              value={reason}
            />
          </label>
          {!challengeId ? (
            <button
              className="dangerButton"
              disabled={
                busy ||
                reason.trim().length < 8 ||
                amountTenge < 1 ||
                amountTenge * 100 > availableMinor
              }
              onClick={() => void begin()}
              type="button"
            >
              Подтвердить возврат
            </button>
          ) : (
            <div className="stepUpInline">
              <input
                aria-label="Код для возврата"
                inputMode="numeric"
                maxLength={6}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
                value={code}
              />
              <button
                disabled={busy || code.length !== 6}
                onClick={() => void confirm()}
                type="button"
              >
                Выполнить
              </button>
            </div>
          )}
          {notice && <small>{notice}</small>}
        </div>
      )}
    </div>
  );
}

function FinanceMetric({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="financeMetric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function FinancePayoutAction({
  kind,
  token,
  subjectId,
  partnerUserId,
  payoutId,
  onChanged,
}: {
  readonly kind: "schedule" | "process";
  readonly token: string;
  readonly subjectId: string;
  readonly partnerUserId?: string;
  readonly payoutId?: string;
  readonly onChanged: () => Promise<void>;
}) {
  const [challengeId, setChallengeId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  async function begin() {
    setBusy(true);
    try {
      const challenge = await requestStaffStepUp(token, "finance.payout", subjectId);
      setChallengeId(challenge.challengeId);
      setNotice("Введите одноразовый код для движения денег.");
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Step-up недоступен");
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    setBusy(true);
    try {
      const grant = await verifyStaffStepUp(token, challengeId, code);
      if (kind === "schedule" && partnerUserId) {
        await scheduleOperationsPayout(token, partnerUserId, grant.stepUpToken);
      } else if (kind === "process" && payoutId) {
        await processOperationsPayout(token, payoutId, grant.stepUpToken);
      }
      setChallengeId("");
      setCode("");
      setNotice("");
      await onChanged();
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Выплата не изменена");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="financePayoutAction">
      {!challengeId ? (
        <button className="quietButton" disabled={busy} onClick={() => void begin()} type="button">
          {kind === "schedule" ? `Выплатить ${partnerUserId?.slice(0, 8)}` : "Отправить провайдеру"}
        </button>
      ) : (
        <div className="stepUpInline">
          <input
            aria-label="Одноразовый код для выплаты"
            inputMode="numeric"
            maxLength={6}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
            value={code}
          />
          <button disabled={busy || code.length !== 6} onClick={() => void confirm()} type="button">
            Подтвердить
          </button>
        </div>
      )}
      {notice && <small>{notice}</small>}
    </div>
  );
}

function financeTypeLabel(type: FinanceOperationsQueue["transactions"][number]["type"]) {
  return {
    adjustment: "Корректировка",
    capture: "Capture заказа",
    payout: "Выплата",
    refund: "Возврат",
    tip: "Чаевые",
  }[type];
}

function financePayoutStatus(status: FinanceOperationsQueue["payouts"][number]["status"]) {
  return {
    failed: "ошибка",
    paid: "выплачено",
    processing: "обработка",
    scheduled: "запланировано",
  }[status];
}

function reconciliationLabel(
  issue: FinanceOperationsQueue["reconciliationCases"][number]["issue"],
) {
  return {
    amount_mismatch: "Не совпала сумма",
    missing_provider_item: "Нет у провайдера",
    unmatched_provider_item: "Нет в ledger",
  }[issue];
}

type SafetyStepUpAction = "incident.sensitive_view" | "incident.restrict" | "incident.close";

function OperationsSafetyCenter({ token }: { readonly token: string }) {
  const [incidents, setIncidents] = useState<readonly SafetyIncidentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    try {
      setIncidents(await loadSafetyIncidents(token));
      setError("");
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Очередь безопасности недоступна");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void reload();
    const timer = window.setInterval(() => void reload(), 10_000);
    return () => window.clearInterval(timer);
  }, [reload]);

  const critical = incidents.filter(
    (incident) => incident.severity === "critical" && incident.status !== "closed",
  ).length;
  const overdue = incidents.filter(
    (incident) => incident.overdue && incident.status !== "closed",
  ).length;

  return (
    <section className="safetyCenter" id="safety" aria-labelledby="safety-title">
      <div className="workspaceHeader">
        <div>
          <p className="eyebrow">Итерация 11 · Trust &amp; Safety</p>
          <h1 id="safety-title">Инциденты и протокол безопасности</h1>
          <p>
            Сигнал, SLA, ответственный и каждый шаг playbook остаются в неизменяемой истории.
            Чувствительные данные открываются только после одноразовой проверки.
          </p>
        </div>
        <div className="dispatchHeaderActions">
          <div className="dispatchMetric dispatchMetric--warning">
            <span>Критические</span>
            <strong>{critical}</strong>
          </div>
          <div
            className={overdue > 0 ? "dispatchMetric dispatchMetric--warning" : "dispatchMetric"}
          >
            <span>Просрочен ответ</span>
            <strong>{overdue}</strong>
          </div>
          <button className="quietButton" onClick={() => void reload()} type="button">
            Обновить
          </button>
        </div>
      </div>
      {error && <div className="notice notice--error">{error}</div>}
      {loading && <div className="notice">Загружаем защищённую очередь…</div>}
      {!loading && incidents.length === 0 && (
        <div className="notice">Открытых и учебных инцидентов пока нет.</div>
      )}
      <div className="safetyGrid">
        {incidents.map((incident) => (
          <OperationsSafetyCard
            incident={incident}
            key={incident.id}
            onChanged={reload}
            token={token}
          />
        ))}
      </div>
    </section>
  );
}

function OperationsSafetyCard({
  incident,
  token,
  onChanged,
}: {
  readonly incident: SafetyIncidentSummary;
  readonly token: string;
  readonly onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [severity, setSeverity] = useState<SafetyIncidentSummary["severity"]>(incident.severity);
  const [stepUpAction, setStepUpAction] = useState<SafetyStepUpAction | null>(null);
  const [challengeId, setChallengeId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [sensitive, setSensitive] = useState<{
    readonly emergencyContact: { readonly displayName: string; readonly phone: string } | null;
    readonly evidence: readonly { readonly mediaId: string; readonly previewUrl: string }[];
  } | null>(null);

  async function perform(action: () => Promise<unknown>) {
    setBusy(true);
    setNotice("");
    try {
      await action();
      await onChanged();
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Действие не выполнено");
    } finally {
      setBusy(false);
    }
  }

  async function beginStepUp(action: SafetyStepUpAction) {
    setBusy(true);
    setNotice("");
    try {
      const challenge = await requestStaffStepUp(token, action, incident.id);
      setChallengeId(challenge.challengeId);
      setStepUpAction(action);
      setVerificationCode("");
      setNotice("Код дополнительной проверки отправлен на подтверждённый номер сотрудника.");
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Проверка недоступна");
    } finally {
      setBusy(false);
    }
  }

  async function confirmStepUp() {
    if (!stepUpAction || verificationCode.length !== 6) return;
    setBusy(true);
    setNotice("");
    try {
      const grant = await verifyStaffStepUp(token, challengeId, verificationCode);
      if (stepUpAction === "incident.sensitive_view") {
        const detail = await viewSensitiveSafetyIncident(
          token,
          incident.id,
          grant.stepUpToken,
          "Разбор активного инцидента",
        );
        setSensitive(detail);
      } else if (stepUpAction === "incident.restrict") {
        await restrictSafetySubject(
          token,
          incident,
          grant.stepUpToken,
          incident.subjectType === "partner" ? "accepting_jobs" : "booking",
        );
      } else {
        await closeSafetyIncident(
          token,
          incident.id,
          grant.stepUpToken,
          "Инцидент закрыт после выполнения обязательного playbook и проверки материалов.",
        );
      }
      setStepUpAction(null);
      setChallengeId("");
      setVerificationCode("");
      await onChanged();
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Проверка не пройдена");
    } finally {
      setBusy(false);
    }
  }

  const missingRequired = incident.playbook.filter((step) => step.required && !step.completedAt);

  return (
    <article className={`panel safetyCard safetyCard--${incident.severity}`}>
      <div className="queueCardHeader">
        <span className={`statusPill statusPill--${incident.status}`}>
          {safetyStatusLabel(incident.status)}
        </span>
        <span className={`severityPill severityPill--${incident.severity}`}>
          {incident.severity === "critical"
            ? "критический"
            : incident.severity === "high"
              ? "высокий"
              : "средний"}
        </span>
        {incident.drill && <span className="drillPill">УЧЕНИЕ · внешние вызовы выключены</span>}
      </div>
      <h2>{safetyKindLabel(incident.kind)}</h2>
      <p>{incident.summary}</p>
      <dl className="queueFacts">
        <div>
          <dt>Ответ до</dt>
          <dd className={incident.overdue ? "dangerText" : ""}>
            {formatDateTime(incident.responseDueAt)}
          </dd>
        </div>
        <div>
          <dt>Заказ</dt>
          <dd>{incident.orderId?.slice(0, 8) ?? "вне заказа"}</dd>
        </div>
        <div>
          <dt>Сторона</dt>
          <dd>{incident.reporterType === "partner" ? "Партнёр" : "Клиент"}</dd>
        </div>
        <div>
          <dt>Материалы</dt>
          <dd>{incident.evidenceCount}</dd>
        </div>
      </dl>

      {!incident.assignedToUserId && incident.status !== "closed" && (
        <button
          className="primaryButton"
          disabled={busy}
          onClick={() => void perform(() => assignSafetyIncident(token, incident.id))}
          type="button"
        >
          Принять инцидент
        </button>
      )}

      {incident.assignedToUserId && incident.status === "signaled" && (
        <div className="safetyActionRow">
          <select
            value={severity}
            onChange={(event) => setSeverity(event.target.value as typeof severity)}
          >
            <option value="medium">Средний</option>
            <option value="high">Высокий</option>
            <option value="critical">Критический</option>
          </select>
          <button
            className="primaryButton"
            disabled={busy}
            onClick={() =>
              void perform(() =>
                triageSafetyIncident(
                  token,
                  incident.id,
                  severity,
                  "Сигнал и SLA подтверждены оператором.",
                ),
              )
            }
            type="button"
          >
            Подтвердить triage
          </button>
        </div>
      )}

      {incident.assignedToUserId && incident.status !== "closed" && (
        <section className="playbook" aria-label="План реагирования">
          <strong>Playbook · осталось обязательных: {missingRequired.length}</strong>
          {incident.playbook.map((step) => (
            <button
              className={step.completedAt ? "playbookStep playbookStep--done" : "playbookStep"}
              disabled={
                busy ||
                Boolean(step.completedAt) ||
                (incident.drill && step.code === "external_emergency_dispatch")
              }
              key={step.code}
              onClick={() =>
                void perform(() =>
                  completeSafetyPlaybookStep(
                    token,
                    incident.id,
                    step.code,
                    "Шаг выполнен и зафиксирован ответственным сотрудником.",
                  ),
                )
              }
              type="button"
            >
              <span aria-hidden="true">{step.completedAt ? "✓" : step.required ? "!" : "○"}</span>
              {step.label}
            </button>
          ))}
        </section>
      )}

      <div className="cardActions">
        <button
          className="quietButton"
          disabled={busy}
          onClick={() => void beginStepUp("incident.sensitive_view")}
          type="button"
        >
          Чувствительные данные
        </button>
        {incident.subjectUserId && incident.status !== "closed" && (
          <button
            className="quietButton"
            disabled={busy}
            onClick={() => void beginStepUp("incident.restrict")}
            type="button"
          >
            Временное ограничение
          </button>
        )}
        {incident.assignedToUserId && !["mitigated", "closed"].includes(incident.status) && (
          <button
            className="quietButton"
            disabled={busy}
            onClick={() =>
              void perform(() =>
                mitigateSafetyIncident(token, incident.id, "Непосредственный риск устранён."),
              )
            }
            type="button"
          >
            Риск устранён
          </button>
        )}
        {incident.status === "mitigated" && (
          <button
            className="primaryButton"
            disabled={busy || missingRequired.length > 0}
            onClick={() => void beginStepUp("incident.close")}
            type="button"
          >
            Закрыть после step-up
          </button>
        )}
      </div>

      {stepUpAction && (
        <fieldset className="stepUpPanel">
          <legend>Дополнительная проверка</legend>
          <label>
            Одноразовый код сотрудника
            <input
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              onChange={(event) => setVerificationCode(event.target.value.replace(/\D/g, ""))}
              value={verificationCode}
            />
          </label>
          <button
            className="primaryButton"
            disabled={busy || verificationCode.length !== 6}
            onClick={() => void confirmStepUp()}
            type="button"
          >
            Подтвердить действие
          </button>
        </fieldset>
      )}

      {sensitive && (
        <div className="sensitivePanel">
          <strong>Доступ записан в журнал</strong>
          <p>
            Контакт:{" "}
            {sensitive.emergencyContact
              ? `${sensitive.emergencyContact.displayName}, ${sensitive.emergencyContact.phone}`
              : "не указан"}
          </p>
          <div className="evidenceStrip">
            {sensitive.evidence.map((evidence) => (
              <a href={evidence.previewUrl} key={evidence.mediaId} rel="noreferrer" target="_blank">
                Защищённый материал {evidence.mediaId.slice(0, 6)}
              </a>
            ))}
          </div>
        </div>
      )}

      {notice && <div className="notice">{notice}</div>}
    </article>
  );
}

function OperationsSupportCenter({ token }: { readonly token: string }) {
  const [cases, setCases] = useState<readonly SupportCaseSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    setError("");
    try {
      setCases(await loadOperationsSupportCases(token));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Очередь поддержки недоступна");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void reload();
    const timer = window.setInterval(() => void reload(), 15_000);
    return () => window.clearInterval(timer);
  }, [reload]);

  const open = cases.filter((supportCase) => supportCase.status !== "resolved").length;
  const joined = cases.filter((supportCase) => supportCase.status === "operator_joined").length;

  return (
    <section className="supportCenter" id="support" aria-labelledby="support-title">
      <div className="workspaceHeader">
        <div>
          <p className="eyebrow">Итерация 10</p>
          <h1 id="support-title">Чат, уведомления и поддержка</h1>
          <p>
            Контекст заказа виден сразу, а история сообщений — только оператору, который явно
            подключился к обращению.
          </p>
        </div>
        <div className="dispatchHeaderActions">
          <div className="dispatchMetric dispatchMetric--warning">
            <span>Открыто</span>
            <strong>{open}</strong>
          </div>
          <div className="dispatchMetric">
            <span>С оператором</span>
            <strong>{joined}</strong>
          </div>
          <button className="quietButton" onClick={() => void reload()} type="button">
            Обновить
          </button>
        </div>
      </div>
      {error && (
        <div className="notice notice--error" role="alert">
          {error}
        </div>
      )}
      {loading && <div className="notice">Загружаем обращения…</div>}
      {!loading && !error && cases.length === 0 && (
        <div className="notice">Открытых обращений пока нет.</div>
      )}
      <div className="supportGrid">
        {cases.map((supportCase) => (
          <OperationsSupportCard
            key={supportCase.id}
            onChanged={reload}
            supportCase={supportCase}
            token={token}
          />
        ))}
      </div>
    </section>
  );
}

function OperationsSupportCard({
  supportCase,
  token,
  onChanged,
}: {
  readonly supportCase: SupportCaseSummary;
  readonly token: string;
  readonly onChanged: () => Promise<void>;
}) {
  const [messages, setMessages] = useState<readonly CommunicationMessageSummary[] | null>(null);
  const [reply, setReply] = useState("");
  const [resolution, setResolution] = useState("Ситуация решена в чате Cleany");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  async function join() {
    setBusy(true);
    setNotice("");
    try {
      const detail = await joinOperationsSupportCase(token, supportCase.id);
      setMessages(detail.messages);
      await onChanged();
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Не удалось подключиться");
    } finally {
      setBusy(false);
    }
  }

  async function openHistory() {
    setBusy(true);
    setNotice("");
    try {
      const detail = await loadOperationsSupportCase(token, supportCase.id);
      setMessages(detail.messages);
    } catch (failure) {
      setNotice(
        failure instanceof Error
          ? failure.message
          : "История доступна только подключённому оператору",
      );
    } finally {
      setBusy(false);
    }
  }

  async function sendReply() {
    if (reply.trim().length === 0) return;
    setBusy(true);
    setNotice("");
    try {
      await sendOperationsSupportMessage(token, supportCase.id, reply.trim());
      setReply("");
      const detail = await loadOperationsSupportCase(token, supportCase.id);
      setMessages(detail.messages);
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Сообщение не отправлено");
    } finally {
      setBusy(false);
    }
  }

  async function resolveCase() {
    setBusy(true);
    setNotice("");
    try {
      await resolveOperationsSupportCase(token, supportCase.id, resolution.trim());
      await onChanged();
      const detail = await loadOperationsSupportCase(token, supportCase.id);
      setMessages(detail.messages);
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Обращение не закрыто");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className={`panel supportCard supportCard--${supportCase.status}`}>
      <div className="queueCardHeader">
        <span className={`statusPill statusPill--${supportCase.status}`}>
          {supportStatusLabel(supportCase.status)}
        </span>
        <strong>{supportCategoryLabel(supportCase.category)}</strong>
      </div>
      <h2>{serviceLabel(supportCase.context.serviceCode)}</h2>
      <p>{supportCase.summary}</p>
      <dl className="queueFacts">
        <div>
          <dt>Заказ</dt>
          <dd>{supportCase.orderId.slice(0, 8)}</dd>
        </div>
        <div>
          <dt>Статус заказа</dt>
          <dd>{supportCase.context.orderStatus}</dd>
        </div>
        <div>
          <dt>Назначение</dt>
          <dd>{supportCase.context.assignmentPresent ? "есть" : "нет"}</dd>
        </div>
        <div>
          <dt>Открыто</dt>
          <dd>{formatDateTime(supportCase.openedAt)}</dd>
        </div>
      </dl>
      {messages === null && supportCase.status === "open" && (
        <button className="primaryButton" disabled={busy} onClick={() => void join()} type="button">
          Подключиться с идентификацией
        </button>
      )}
      {messages === null && supportCase.status !== "open" && (
        <button
          className="quietButton"
          disabled={busy}
          onClick={() => void openHistory()}
          type="button"
        >
          Открыть разрешённую историю
        </button>
      )}
      {messages !== null && (
        <section className="supportConversation" aria-label="История обращения">
          {messages.map((message) => (
            <div
              className={`supportMessage supportMessage--${message.sender.type}`}
              key={message.id}
            >
              <strong>{message.sender.label}</strong>
              <p>{message.body}</p>
              {message.mediaIds.length > 0 && (
                <small>Защищённых вложений: {message.mediaIds.length}</small>
              )}
            </div>
          ))}
          {supportCase.status !== "resolved" && (
            <>
              <label className="reviewField">
                Сообщение от оператора Cleany
                <textarea
                  maxLength={1000}
                  onChange={(event) => setReply(event.target.value)}
                  rows={3}
                  value={reply}
                />
              </label>
              <button
                className="primaryButton"
                disabled={busy || reply.trim().length === 0}
                onClick={() => void sendReply()}
                type="button"
              >
                Отправить в чат заказа
              </button>
              <label className="reviewField">
                Итог обращения
                <textarea
                  minLength={4}
                  onChange={(event) => setResolution(event.target.value)}
                  rows={2}
                  value={resolution}
                />
              </label>
              <button
                className="quietButton"
                disabled={busy || resolution.trim().length < 4}
                onClick={() => void resolveCase()}
                type="button"
              >
                Закрыть обращение
              </button>
            </>
          )}
        </section>
      )}
      {notice && (
        <p className="formError" role="alert">
          {notice}
        </p>
      )}
    </article>
  );
}

function supportStatusLabel(status: SupportCaseSummary["status"]): string {
  return { open: "Новое", operator_joined: "Оператор подключён", resolved: "Закрыто" }[status];
}

function supportCategoryLabel(category: SupportCaseSummary["category"]): string {
  return {
    access: "Доступ",
    arrival: "Прибытие",
    other: "Другое",
    quality: "Качество",
    safety: "Безопасность",
    scope: "Объём работ",
  }[category];
}

function safetyStatusLabel(status: SafetyIncidentSummary["status"]): string {
  return {
    closed: "Закрыт",
    investigating: "В работе",
    mitigated: "Риск устранён",
    signaled: "Новый сигнал",
    triaged: "Проверен",
  }[status];
}

function safetyKindLabel(kind: SafetyIncidentSummary["kind"]): string {
  return {
    account_compromise: "Компрометация аккаунта",
    damage: "Повреждение имущества",
    lost_device: "Потеря устройства",
    misconduct: "Ненадлежащее поведение",
    other: "Другой инцидент",
    provider_outage: "Сбой провайдера",
    sos: "SOS · срочный сигнал",
  }[kind];
}

function OperationsQualityCenter({ token }: { readonly token: string }) {
  const [jobs, setJobs] = useState<readonly QualityJobSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    setError("");
    try {
      setJobs(await loadOperationsQualityJobs(token));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Контроль качества недоступен");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void reload();
    const timer = window.setInterval(() => void reload(), 20_000);
    return () => window.clearInterval(timer);
  }, [reload]);

  const openClaims = jobs.reduce(
    (total, job) => total + job.claims.filter((claim) => claim.status === "open").length,
    0,
  );
  const awaitingAcceptance = jobs.filter((job) => job.state === "awaiting_acceptance").length;

  return (
    <section className="qualityCenter" id="quality" aria-labelledby="quality-title">
      <div className="workspaceHeader">
        <div>
          <p className="eyebrow">Итерация 9</p>
          <h1 id="quality-title">Качество, приёмка и претензии</h1>
          <p>
            Версия стандарта, доказательства, решения клиента и повторная уборка в одной истории.
          </p>
        </div>
        <div className="dispatchHeaderActions">
          <div className="dispatchMetric">
            <span>На приёмке</span>
            <strong>{awaitingAcceptance}</strong>
          </div>
          <div className="dispatchMetric dispatchMetric--warning">
            <span>Претензии</span>
            <strong>{openClaims}</strong>
          </div>
          <button className="quietButton" onClick={() => void reload()} type="button">
            Обновить
          </button>
        </div>
      </div>
      {error && (
        <div className="notice notice--error" role="alert">
          {error}
        </div>
      )}
      {loading && <div className="notice">Загружаем контроль качества…</div>}
      {!loading && !error && jobs.length === 0 && (
        <div className="notice">Заказов на контроле качества пока нет.</div>
      )}
      <div className="qualityGrid">
        {jobs.map((job) => (
          <OperationsQualityCard job={job} key={job.order.id} onChanged={reload} token={token} />
        ))}
      </div>
    </section>
  );
}

function OperationsQualityCard({
  job,
  token,
  onChanged,
}: {
  readonly job: QualityJobSummary;
  readonly token: string;
  readonly onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [reason, setReason] = useState("Проверили чек-лист, доказательства и обращение клиента");
  const [resolution, setResolution] = useState<"reclean" | "compensation" | "dismissed">("reclean");
  const [compensation, setCompensation] = useState(1_000);
  const [scheduledAt, setScheduledAt] = useState(() => {
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
    return tomorrow.toISOString().slice(0, 16);
  });
  const openClaim = job.claims.find((claim) => claim.status === "open");
  const completed = job.checklist.filter((item) => item.status === "done").length;
  const evidence = job.checklist.reduce((total, item) => total + item.evidence.length, 0);

  async function resolveClaim() {
    if (!openClaim) return;
    setBusy(true);
    setNotice("");
    try {
      await resolveOperationsQualityClaim(token, job.order.id, openClaim.id, {
        expectedRevision: job.revision,
        reason,
        resolution,
        ...(resolution === "compensation"
          ? { compensationMinor: Math.round(compensation * 100) }
          : {}),
        ...(resolution === "reclean" ? { scheduledAt: new Date(scheduledAt).toISOString() } : {}),
      });
      await onChanged();
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Решение не сохранено");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className={`panel qualityCard ${openClaim ? "qualityCard--claim" : ""}`}>
      <div className="queueCardHeader">
        <span className={`statusPill statusPill--${job.state}`}>
          {qualityStateLabel(job.state)}
        </span>
        <strong>{job.assignment.partner.displayName}</strong>
      </div>
      <h2>{serviceLabel(job.order.serviceCode)}</h2>
      <p>
        Стандарт {job.standardVersion} · ревизия {job.revision}
      </p>
      <dl className="queueFacts">
        <div>
          <dt>Чек-лист</dt>
          <dd>
            {completed}/{job.checklist.length}
          </dd>
        </div>
        <div>
          <dt>Доказательства</dt>
          <dd>{evidence}</dd>
        </div>
        <div>
          <dt>Согласовано</dt>
          <dd>{formatMoney(job.agreedTotalMinor)}</dd>
        </div>
        <div>
          <dt>Повтор</dt>
          <dd>{job.remediationRound}</dd>
        </div>
      </dl>
      <ul className="qualityChecklist" aria-label="Чек-лист качества">
        {job.checklist.map((item) => (
          <li key={item.code}>
            <span aria-hidden="true">{item.status === "done" ? "✓" : "!"}</span>
            <p>
              <strong>{item.labels.ru}</strong>
              <small>
                {item.evidence.length} фото · предыдущих версий {item.historyCount}
              </small>
            </p>
          </li>
        ))}
      </ul>
      {job.scopeChanges.length > 0 && (
        <div className="qualityScopeHistory">
          <strong>Дополнительный объём</strong>
          {job.scopeChanges.map((change) => (
            <p key={change.id}>
              {change.description} · {formatMoney(change.amountMinor)} ·{" "}
              {scopeStatusLabel(change.status)}
            </p>
          ))}
        </div>
      )}
      {openClaim && (
        <div className="qualityClaim">
          <strong>Претензия: {claimCategoryLabel(openClaim.category)}</strong>
          <p>{openClaim.reason}</p>
          <label className="reviewField">
            Решение
            <select
              onChange={(event) =>
                setResolution(event.target.value as "reclean" | "compensation" | "dismissed")
              }
              value={resolution}
            >
              <option value="reclean">Повторная уборка</option>
              <option value="compensation">Компенсация</option>
              <option value="dismissed">Отклонить после проверки</option>
            </select>
          </label>
          {resolution === "reclean" && (
            <label className="reviewField">
              Время повторной уборки
              <input
                min={new Date().toISOString().slice(0, 16)}
                onChange={(event) => setScheduledAt(event.target.value)}
                type="datetime-local"
                value={scheduledAt}
              />
            </label>
          )}
          {resolution === "compensation" && (
            <label className="reviewField">
              Компенсация, ₸
              <input
                min={1}
                onChange={(event) => setCompensation(Number(event.target.value))}
                type="number"
                value={compensation}
              />
            </label>
          )}
          <label className="reviewField">
            Обоснование
            <textarea
              minLength={4}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              value={reason}
            />
          </label>
          <button
            className="primaryButton qualityResolveButton"
            disabled={busy || reason.trim().length < 4 || !scheduledAt}
            onClick={() => void resolveClaim()}
            type="button"
          >
            Сохранить решение
          </button>
        </div>
      )}
      {job.claims
        .filter((claim) => claim.status === "resolved")
        .map((claim) => (
          <p className="qualityResolution" key={claim.id}>
            Решено: {claim.resolution?.type ?? "—"} · исходные доказательства сохранены
          </p>
        ))}
      {notice && (
        <p className="formError" role="alert">
          {notice}
        </p>
      )}
    </article>
  );
}

function qualityStateLabel(state: QualityJobSummary["state"]): string {
  return {
    accepted: "Принят",
    awaiting_acceptance: "На приёмке",
    in_progress: "В работе",
    quality_review: "Претензия",
    remediation: "Повторная уборка",
  }[state];
}

function scopeStatusLabel(status: QualityJobSummary["scopeChanges"][number]["status"]): string {
  return { approved: "согласовано", pending: "ожидает", rejected: "отклонено" }[status];
}

function claimCategoryLabel(category: QualityJobSummary["claims"][number]["category"]): string {
  return {
    damage: "повреждение",
    not_completed: "не выполнено",
    other: "другое",
    quality: "качество",
  }[category];
}

function OperationsActiveJobCenter({ token }: { readonly token: string }) {
  const [jobs, setJobs] = useState<readonly OperationsActiveJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    setError("");
    try {
      setJobs(await loadOperationsActiveJobs(token));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Активные заказы недоступны");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void reload();
    const timer = window.setInterval(() => void reload(), 15_000);
    return () => window.clearInterval(timer);
  }, [reload]);

  const openAlerts = jobs.reduce(
    (count, job) => count + job.alerts.filter((alert) => alert.status === "open").length,
    0,
  );

  return (
    <section className="activeJobCenter" id="active-jobs" aria-labelledby="active-jobs-title">
      <div className="workspaceHeader">
        <div>
          <p className="eyebrow">Итерация 8</p>
          <h1 id="active-jobs-title">Активные уборки</h1>
          <p>Прибытие, ETA, начало, офлайн-синхронизация и исключения в одной хронологии.</p>
        </div>
        <div className="dispatchHeaderActions">
          <div className="dispatchMetric">
            <span>Активно</span>
            <strong>{jobs.length}</strong>
          </div>
          <div className="dispatchMetric dispatchMetric--warning">
            <span>Сигналы</span>
            <strong>{openAlerts}</strong>
          </div>
          <button className="quietButton" onClick={() => void reload()} type="button">
            Обновить
          </button>
        </div>
      </div>
      {error && (
        <div className="notice notice--error" role="alert">
          {error}
        </div>
      )}
      {loading && <div className="notice">Загружаем активные уборки…</div>}
      {!loading && !error && jobs.length === 0 && (
        <div className="notice">Активных уборок сейчас нет.</div>
      )}
      <div className="activeJobGrid">
        {jobs.map((job) => (
          <OperationsActiveJobCard job={job} key={job.order.id} onChanged={reload} token={token} />
        ))}
      </div>
    </section>
  );
}

function OperationsActiveJobCard({
  job,
  token,
  onChanged,
}: {
  readonly job: OperationsActiveJob;
  readonly token: string;
  readonly onChanged: () => Promise<void>;
}) {
  const [reason, setReason] = useState("Связались с партнёром, ситуация проверена");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const openAlerts = job.alerts.filter((alert) => alert.status === "open");
  const lastEvent = job.timeline.at(-1);

  async function resolve(alertId: string) {
    setBusy(true);
    setNotice("");
    try {
      await resolveOperationsActiveJobAlert(token, job.order.id, alertId, reason);
      await onChanged();
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Сигнал не закрыт");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article
      className={`panel activeJobCard ${openAlerts.length > 0 ? "activeJobCard--alert" : ""}`}
    >
      <div className="queueCardHeader">
        <span className={`statusPill statusPill--${job.order.status}`}>
          {activeJobStatusLabel(job.order.status)}
        </span>
        <strong>{job.assignment.partner.displayName}</strong>
      </div>
      <h2>{serviceLabel(job.order.serviceCode)}</h2>
      <p>
        {job.order.address.street}, {job.order.address.building} ·{" "}
        {formatDateTime(job.order.slotStart)}
      </p>
      <dl className="queueFacts">
        <div>
          <dt>ETA</dt>
          <dd>{job.route.etaMinutes === null ? "—" : `${job.route.etaMinutes} мин`}</dd>
        </div>
        <div>
          <dt>Дистанция</dt>
          <dd>
            {job.route.distanceMeters === null ? "—" : formatDistance(job.route.distanceMeters)}
          </dd>
        </div>
        <div>
          <dt>Последнее событие</dt>
          <dd>{lastEvent ? activeJobEventLabel(lastEvent.type) : "Назначение"}</dd>
        </div>
      </dl>
      {lastEvent && lastEvent.syncDelaySeconds > 30 && (
        <p className="offlineEventNote">
          Событие пришло офлайн через {Math.ceil(lastEvent.syncDelaySeconds / 60)} мин.
        </p>
      )}
      {openAlerts.length > 0 && (
        <div className="activeJobAlerts">
          {openAlerts.map((alert) => (
            <div className="activeJobAlert" key={alert.id}>
              <strong>{activeJobAlertLabel(alert.type)}</strong>
              <span>{formatDateTime(alert.raisedAt)}</span>
              <button
                className="quietButton"
                disabled={busy || reason.trim().length < 4}
                onClick={() => void resolve(alert.id)}
                type="button"
              >
                Закрыть сигнал
              </button>
            </div>
          ))}
          <label className="reviewField">
            Результат проверки
            <textarea
              minLength={4}
              onChange={(event) => setReason(event.target.value)}
              rows={2}
              value={reason}
            />
          </label>
        </div>
      )}
      {notice && (
        <p className="formError" role="alert">
          {notice}
        </p>
      )}
      <ul className="activeJobTimeline" aria-label="Хронология заказа">
        {job.timeline.slice(-4).map((event) => (
          <li key={event.id}>
            <span aria-hidden="true" />
            <p>
              <strong>{activeJobEventLabel(event.type)}</strong>
              <small>{formatDateTime(event.occurredAt)}</small>
            </p>
          </li>
        ))}
      </ul>
    </article>
  );
}

function activeJobStatusLabel(status: OperationsActiveJob["order"]["status"]): string {
  return {
    arrived: "На месте",
    confirmed: "Назначен",
    en_route: "В пути",
    in_progress: "В работе",
  }[status];
}

function activeJobEventLabel(type: OperationsActiveJob["timeline"][number]["type"]): string {
  return {
    alert_resolved: "Сигнал закрыт",
    arrived: "Прибыл",
    emergency_stop: "Срочная остановка",
    en_route: "Выехал",
    location: "Геопозиция",
    start: "Начал работу",
  }[type];
}

function activeJobAlertLabel(type: OperationsActiveJob["alerts"][number]["type"]): string {
  return {
    connection_lost: "Потеря связи",
    emergency_stop: "Срочная остановка",
    late: "Опоздание",
    no_show: "Неявка",
  }[type];
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat("ru-KZ", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatDistance(meters: number): string {
  return meters < 1_000 ? `${meters} м` : `${(meters / 1_000).toFixed(1)} км`;
}

function PartnerApplicationsReview({ token }: { readonly token: string }) {
  const [applications, setApplications] = useState<readonly PartnerApplicationSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState<PartnerApplicationSummary["status"] | "">("");
  const [query, setQuery] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setApplications(await loadPartnerApplications(token, status || undefined));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Заявки недоступны");
    } finally {
      setLoading(false);
    }
  }, [status, token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const applyApplication = useCallback(
    (updated: PartnerApplicationSummary) => {
      setApplications((current) => {
        if (status && updated.status !== status) {
          return current.filter((application) => application.id !== updated.id);
        }
        return current.map((application) =>
          application.id === updated.id ? updated : application,
        );
      });
    },
    [status],
  );

  const normalizedQuery = query.trim().toLocaleLowerCase("ru");
  const visibleApplications = applications.filter((application) =>
    [
      application.identity?.legalName ?? "",
      application.identity?.iinMasked ?? "",
      application.id,
    ].some((value) => value.toLocaleLowerCase("ru").includes(normalizedQuery)),
  );

  return (
    <section className="partnerReview" id="partners" aria-labelledby="partner-review-title">
      <div className="workspaceHeader">
        <div>
          <p className="eyebrow">Итерация 6</p>
          <h1 id="partner-review-title">Допуск партнёров</h1>
          <p>
            Документы, обучение, контрольное задание и уровни собраны в одной проверяемой истории.
          </p>
        </div>
        <button className="quietButton" onClick={() => void reload()} type="button">
          Обновить
        </button>
      </div>
      <div className="partnerFilters panel">
        <label>
          <span>Статус анкеты</span>
          <select
            onChange={(event) =>
              setStatus(event.target.value as PartnerApplicationSummary["status"] | "")
            }
            value={status}
          >
            <option value="">Все анкеты</option>
            <option value="draft">Черновики</option>
            <option value="submitted">На проверке</option>
            <option value="needs_changes">На доработке</option>
            <option value="approved">Допущенные</option>
            <option value="rejected">Отклонённые</option>
            <option value="restricted">Ограниченные</option>
          </select>
        </label>
        <label>
          <span>Поиск</span>
          <input
            onChange={(event) => setQuery(event.target.value)}
            placeholder="ФИО, маска ИИН или ID"
            value={query}
          />
        </label>
      </div>
      {error && (
        <div className="notice notice--error" role="alert">
          {error}
        </div>
      )}
      {loading && <div className="notice">Загружаем заявки…</div>}
      {!loading && !error && visibleApplications.length === 0 && (
        <div className="notice">Заявок пока нет.</div>
      )}
      <div className="partnerReviewGrid">
        {visibleApplications.map((application) => (
          <PartnerApplicationCard
            application={application}
            key={application.id}
            onChanged={applyApplication}
            token={token}
          />
        ))}
      </div>
    </section>
  );
}

function PartnerApplicationCard({
  application,
  token,
  onChanged,
}: {
  readonly application: PartnerApplicationSummary;
  readonly token: string;
  readonly onChanged: (application: PartnerApplicationSummary) => void;
}) {
  const [reason, setReason] = useState("Данные и документы проверены");
  const [score, setScore] = useState(90);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [saved, setSaved] = useState(false);

  async function review(
    action: "request_changes" | "schedule_control_task" | "approve" | "reject" | "restrict",
  ) {
    setBusy(true);
    setNotice("");
    setSaved(false);
    try {
      const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      const nextMonth = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
      const updated = await reviewPartnerApplication(token, application.id, {
        action,
        reason,
        ...(action === "schedule_control_task" ? { scheduledAt: tomorrow } : {}),
        ...(action === "approve" ? { controlTaskScore: score } : {}),
        ...(action === "reject" ? { reapplyAt: nextMonth } : {}),
      });
      onChanged(updated);
      setSaved(true);
      setNotice("Решение сохранено");
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Решение не сохранено");
    } finally {
      setBusy(false);
    }
  }

  async function level(level: "verified" | "pro" | "restricted") {
    setBusy(true);
    setNotice("");
    setSaved(false);
    try {
      const updated = await setPartnerLevel(token, application.id, level, reason);
      onChanged(updated);
      setSaved(true);
      setNotice("Уровень обновлён");
    } catch (failure) {
      setNotice(failure instanceof Error ? failure.message : "Уровень не изменён");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="panel partnerApplicationCard">
      <div className="queueCardHeader">
        <span className={`statusPill statusPill--${application.status}`}>
          {applicationStatusLabel(application.status)}
        </span>
        <strong>{partnerLevelLabel(application.level)}</strong>
      </div>
      <h2>{application.identity?.legalName ?? "Новый кандидат"}</h2>
      <p>
        {application.identity?.city ?? "Город не заполнен"} ·{" "}
        {application.identity?.iinMasked ?? "ИИН —"}
      </p>
      <div
        className="applicationProgress"
        role="progressbar"
        aria-label={`Готово ${application.progress.percent}%`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={application.progress.percent}
      >
        <span style={{ width: `${application.progress.percent}%` }} />
      </div>
      <dl className="queueFacts">
        <div>
          <dt>Документы</dt>
          <dd>{application.documents.length}/3</dd>
        </div>
        <div>
          <dt>Тест</dt>
          <dd>{application.training.testScore ?? "—"}%</dd>
        </div>
        <div>
          <dt>Контроль</dt>
          <dd>{application.controlTask.state}</dd>
        </div>
      </dl>
      {application.reviewReason && <p className="reviewReason">{application.reviewReason}</p>}
      <label className="reviewField">
        Причина решения
        <textarea
          minLength={4}
          onChange={(event) => setReason(event.target.value)}
          rows={2}
          value={reason}
        />
      </label>
      {application.controlTask.state === "scheduled" && (
        <label className="reviewField">
          Балл контрольного задания
          <input
            max={100}
            min={0}
            onChange={(event) => setScore(Number(event.target.value))}
            type="number"
            value={score}
          />
        </label>
      )}
      {notice && (
        <p className={saved ? "formNotice" : "formError"} role={saved ? "status" : "alert"}>
          {notice}
        </p>
      )}
      <div className="reviewActions">
        {application.status === "submitted" && application.controlTask.state !== "scheduled" && (
          <button
            className="primaryButton"
            disabled={busy}
            onClick={() => void review("schedule_control_task")}
            type="button"
          >
            Назначить контроль
          </button>
        )}
        {application.status === "submitted" && application.controlTask.state === "scheduled" && (
          <button
            className="primaryButton"
            disabled={busy || score < 80}
            onClick={() => void review("approve")}
            type="button"
          >
            Допустить новичком
          </button>
        )}
        {application.status === "submitted" && (
          <button
            className="quietButton"
            disabled={busy}
            onClick={() => void review("request_changes")}
            type="button"
          >
            На доработку
          </button>
        )}
        {application.status === "approved" && application.level === "newcomer" && (
          <button
            className="quietButton"
            disabled={busy}
            onClick={() => void level("verified")}
            type="button"
          >
            Подтвердить уровень
          </button>
        )}
        {application.status === "approved" && application.level === "verified" && (
          <button
            className="quietButton"
            disabled={busy}
            onClick={() => void level("pro")}
            type="button"
          >
            Повысить до Профи
          </button>
        )}
        {(application.status === "submitted" || application.status === "approved") && (
          <button
            className="dangerButton"
            disabled={busy}
            onClick={() =>
              void (application.status === "approved" ? level("restricted") : review("reject"))
            }
            type="button"
          >
            {application.status === "approved" ? "Ограничить" : "Отклонить"}
          </button>
        )}
      </div>
    </article>
  );
}

function applicationStatusLabel(status: PartnerApplicationSummary["status"]): string {
  return {
    approved: "Допущен",
    draft: "Черновик",
    needs_changes: "Доработка",
    rejected: "Отклонён",
    restricted: "Ограничен",
    submitted: "На проверке",
  }[status];
}

function partnerLevelLabel(level: PartnerApplicationSummary["level"]): string {
  return {
    candidate: "Кандидат",
    newcomer: "Новичок",
    pro: "Профи",
    restricted: "Ограничен",
    review: "Проверка",
    verified: "Проверен",
  }[level];
}

function OperationsOrderQueue({ token }: { readonly token: string }) {
  const [orders, setOrders] = useState<readonly OperationsOrder[]>([]);
  const [metrics, setMetrics] = useState<DispatchMetrics | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<OperationsOrder["status"]>("searching");
  const [query, setQuery] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [nextOrders, nextMetrics] = await Promise.all([
        loadOperationsOrders(token, status),
        loadDispatchMetrics(token),
      ]);
      setOrders(nextOrders);
      setMetrics(nextMetrics);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Очередь недоступна");
    } finally {
      setLoading(false);
    }
  }, [status, token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const normalizedQuery = query.trim().toLocaleLowerCase("ru");
  const visibleOrders = orders.filter((order) =>
    [
      order.id,
      order.receiptNumber,
      order.serviceCode,
      order.assignment?.partner.displayName ?? "",
    ].some((value) => value.toLocaleLowerCase("ru").includes(normalizedQuery)),
  );

  return (
    <section className="orderQueue" id="orders" aria-labelledby="order-queue-title">
      <div className="workspaceHeader">
        <div>
          <p className="eyebrow">Order lifecycle</p>
          <h1 id="order-queue-title">Все заказы</h1>
          <p>От поиска исполнителя до приёмки, отмены и финансового завершения.</p>
        </div>
        <div className="dispatchHeaderActions">
          {metrics && (
            <div className="dispatchMetric">
              <span>Fill rate</span>
              <strong>{metrics.fillRatePercent}%</strong>
            </div>
          )}
          <button className="quietButton" onClick={() => void reload()} type="button">
            Обновить
          </button>
        </div>
      </div>
      <div className="orderFilters panel">
        <label>
          <span>Статус</span>
          <select
            onChange={(event) => setStatus(event.target.value as OperationsOrder["status"])}
            value={status}
          >
            {(
              [
                "searching",
                "confirmed",
                "en_route",
                "arrived",
                "in_progress",
                "awaiting_acceptance",
                "quality_review",
                "remediation",
                "accepted",
                "cancelled",
              ] as const
            ).map((candidate) => (
              <option key={candidate} value={candidate}>
                {orderStatusLabel(candidate)}
              </option>
            ))}
          </select>
        </label>
        <label className="orderSearchField">
          <span>Поиск</span>
          <input
            onChange={(event) => setQuery(event.target.value)}
            placeholder="ID, квитанция, услуга или партнёр"
            value={query}
          />
        </label>
      </div>
      {error && (
        <div className="notice notice--error" role="alert">
          {error}
        </div>
      )}
      {loading && <div className="notice">Обновляем очередь…</div>}
      {!loading && !error && visibleOrders.length === 0 && (
        <div className="notice">Заказов с выбранным статусом нет.</div>
      )}
      <div className="orderQueueGrid">
        {visibleOrders.map((order) =>
          order.status === "searching" ? (
            <DispatchOrderCard key={order.id} onChanged={reload} order={order} token={token} />
          ) : (
            <OperationsOrderHistoryCard key={order.id} order={order} />
          ),
        )}
      </div>
    </section>
  );
}

function OperationsOrderHistoryCard({ order }: { readonly order: OperationsOrder }) {
  return (
    <article className="panel orderQueueCard orderHistoryCard">
      <div className="queueCardHeader">
        <span className={`statusPill statusPill--${order.status}`}>
          {orderStatusLabel(order.status)}
        </span>
        <strong>{formatMoney(order.totalMinor)}</strong>
      </div>
      <h2>{serviceLabel(order.serviceCode)}</h2>
      <p>
        {formatDateTime(order.slotStart)} · {order.durationMinutes} мин
      </p>
      <dl className="queueFacts">
        <div>
          <dt>Квитанция</dt>
          <dd>{order.receiptNumber}</dd>
        </div>
        <div>
          <dt>Оплата</dt>
          <dd>{paymentStatusLabel(order.payment.status)}</dd>
        </div>
        <div>
          <dt>Зона</dt>
          <dd>{order.zoneCode}</dd>
        </div>
      </dl>
      <div className="orderAddress">
        <span>Адрес</span>
        <strong>
          {order.address.street}, {order.address.building}
          {order.address.apartment ? `, кв. ${order.address.apartment}` : ""}
        </strong>
      </div>
      <div className="assignmentSummary">
        <span>{order.assignment ? "Исполнитель" : "Исполнитель не назначен"}</span>
        {order.assignment && (
          <strong>
            {order.assignment.partner.displayName} ·{" "}
            {order.assignment.source === "manual" ? "вручную" : "авто"}
          </strong>
        )}
      </div>
      <code className="orderId">{order.id}</code>
    </article>
  );
}

function DispatchOrderCard({
  order,
  token,
  onChanged,
}: {
  readonly order: OperationsOrder;
  readonly token: string;
  readonly onChanged: () => Promise<void>;
}) {
  const [dispatch, setDispatch] = useState<OperationsDispatchState | null>(null);
  const [reason, setReason] = useState("Ручное назначение после подтверждения доступности");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reloadDispatch = useCallback(async () => {
    try {
      setDispatch(await loadOperationsDispatch(token, order.id));
      setError("");
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Подбор недоступен");
    }
  }, [order.id, token]);

  useEffect(() => {
    void reloadDispatch();
  }, [reloadDispatch]);

  async function restart() {
    setBusy(true);
    try {
      await restartOperationsDispatch(token, order.id, reason);
      await reloadDispatch();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Волна не запущена");
    } finally {
      setBusy(false);
    }
  }

  async function assign(partnerUserId: string) {
    setBusy(true);
    try {
      await manuallyAssignOrder(token, order.id, partnerUserId, reason);
      await onChanged();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Назначение не выполнено");
    } finally {
      setBusy(false);
    }
  }

  const activeOffers = dispatch?.offers.filter((offer) => offer.status === "offered") ?? [];
  return (
    <article className="panel orderQueueCard">
      <div className="queueCardHeader">
        <span className="statusPill">Ищем клинера</span>
        <strong>{formatMoney(order.totalMinor)}</strong>
      </div>
      <h2>{serviceLabel(order.serviceCode)}</h2>
      <p>
        {new Date(order.slotStart).toLocaleString("ru-KZ", {
          dateStyle: "medium",
          timeStyle: "short",
          timeZone: "Asia/Almaty",
        })}
      </p>
      <p className="privacyHint">Точный адрес скрыт от партнёров до назначения.</p>
      <dl className="queueFacts">
        <div>
          <dt>Активные офферы</dt>
          <dd>{activeOffers.length}</dd>
        </div>
        <div>
          <dt>Волна</dt>
          <dd>{Math.max(0, ...(dispatch?.offers.map((offer) => offer.wave) ?? [])) || "—"}</dd>
        </div>
        <div>
          <dt>Eligible</dt>
          <dd>{dispatch?.candidates.length ?? "…"}</dd>
        </div>
      </dl>
      <label className="reviewField">
        Причина ручного действия
        <textarea rows={2} value={reason} onChange={(event) => setReason(event.target.value)} />
      </label>
      {error && <p className="formError">{error}</p>}
      {dispatch?.candidates.slice(0, 3).map((candidate) => (
        <button
          className="candidateButton"
          disabled={busy}
          key={candidate.partnerUserId}
          onClick={() => void assign(candidate.partnerUserId)}
          type="button"
        >
          <span>
            <strong>{candidate.displayName}</strong>
            <small>
              {partnerLevelLabel(candidate.level)} · score {candidate.rankingScore} · ETA ≈{" "}
              {candidate.rankingFactors.estimatedEtaMinutes} мин
            </small>
          </span>
          <span>Назначить</span>
        </button>
      ))}
      <button
        className="quietButton dispatchRestart"
        disabled={busy || reason.trim().length < 4}
        onClick={() => void restart()}
        type="button"
      >
        Новая волна
      </button>
    </article>
  );
}

function serviceLabel(code: string): string {
  return (
    {
      maintenance: "Поддерживающая уборка",
      general: "Генеральная уборка",
      post_renovation: "После ремонта",
    }[code] ?? code
  );
}

function orderStatusLabel(status: OperationsOrder["status"]): string {
  return {
    accepted: "Принят",
    arrived: "Прибыл",
    awaiting_acceptance: "Ожидает приёмки",
    cancelled: "Отменён",
    confirmed: "Подтверждён",
    en_route: "В пути",
    in_progress: "В работе",
    quality_review: "Проверка качества",
    remediation: "Исправление",
    searching: "Ищем исполнителя",
  }[status];
}

function paymentStatusLabel(status: OperationsOrder["payment"]["status"]): string {
  return {
    authorized: "Авторизована",
    failed: "Ошибка",
    voided: "Отменена",
  }[status];
}

function OperationsSignIn({
  onSignedIn,
}: {
  readonly onSignedIn: (session: import("@cleany/platform-contracts").SessionTokens) => void;
}) {
  const [phone, setPhone] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (!challengeId) {
        const challenge = await requestOperationsOtp(phone);
        setChallengeId(challenge.challengeId);
      } else {
        const session = await verifyOperationsOtp(challengeId, code);
        if (
          !session.profile.roles.some((role) => ["operator", "supervisor", "owner"].includes(role))
        ) {
          throw new Error("У аккаунта нет доступа к Operations.");
        }
        onSignedIn(session);
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Не удалось войти");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="authPanel" aria-labelledby="sign-in-title">
      <p className="eyebrow">Защищённый вход</p>
      <h1 id="sign-in-title">Cleany Operations</h1>
      <p>Войдите по номеру телефона сотрудника. Доступ проверяется серверными ролями.</p>
      <form className="formStack" onSubmit={submit}>
        {!challengeId ? (
          <label>
            Телефон
            <input
              autoComplete="tel"
              onChange={(event) => setPhone(event.target.value)}
              required
              value={phone}
            />
          </label>
        ) : (
          <label>
            Код из SMS
            <input
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              minLength={6}
              onChange={(event) => setCode(event.target.value)}
              required
              value={code}
            />
          </label>
        )}
        {error && (
          <p className="formError" role="alert">
            {error}
          </p>
        )}
        <button className="primaryButton" disabled={busy} type="submit">
          {busy ? "Подождите…" : challengeId ? "Войти" : "Получить код"}
        </button>
      </form>
    </section>
  );
}

function CatalogWorkspace({
  token,
  locale,
  onUnauthorized,
}: {
  readonly token: string;
  readonly locale: OperationsLocale;
  readonly onUnauthorized: () => void;
}) {
  const [catalog, setCatalog] = useState<ServiceCatalog | null>(null);
  const [error, setError] = useState("");
  const [selectedCode, setSelectedCode] = useState("");

  const reload = useCallback(async () => {
    setError("");
    try {
      const next = await loadOperationsCatalog(token, locale);
      setCatalog(next);
      setSelectedCode((current) => current || next.services[0]?.code || "");
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : "Каталог недоступен";
      setError(message);
      if (/unauthorized|token|401/i.test(message)) onUnauthorized();
    }
  }, [locale, onUnauthorized, token]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const service = catalog?.services.find((item) => item.code === selectedCode);
  return (
    <>
      <section className="workspaceHeader">
        <div>
          <p className="eyebrow">Итерация 4</p>
          <h1>Каталог и фиксированные цены</h1>
          <p>Каждое изменение создаёт новую версию. Старые котировки остаются воспроизводимыми.</p>
        </div>
        {catalog && (
          <div className="versionBadge">
            <span>Активная версия</span>
            <strong>{catalog.pricingVersion}</strong>
          </div>
        )}
      </section>
      {error && (
        <div className="notice notice--error" role="alert">
          {error}
        </div>
      )}
      {!catalog && !error && <div className="notice">Загружаем каталог…</div>}
      {catalog && (
        <div className="catalogGrid">
          <section className="panel serviceList" aria-label="Услуги">
            <p className="cardLabel">Услуги</p>
            {catalog.services.map((item) => (
              <button
                className={
                  item.code === selectedCode
                    ? "serviceButton serviceButton--active"
                    : "serviceButton"
                }
                key={item.code}
                onClick={() => setSelectedCode(item.code)}
                type="button"
              >
                <strong>{item.name}</strong>
                <span>от {formatMoney(item.basePriceMinor)}</span>
              </button>
            ))}
            <div className="zoneSummary" id="zones">
              <p className="cardLabel">Активные зоны</p>
              {catalog.zones.map((zone) => (
                <span key={zone.code}>{zone.name}</span>
              ))}
            </div>
          </section>
          {service && (
            <CatalogEditor
              key={`${catalog.pricingVersion}:${service.code}`}
              service={service}
              token={token}
              onPublished={reload}
            />
          )}
        </div>
      )}
    </>
  );
}

function CatalogEditor({
  service,
  token,
  onPublished,
}: {
  readonly service: CatalogService;
  readonly token: string;
  readonly onPublished: () => Promise<void>;
}) {
  const [revision, setRevision] = useState<CatalogServiceRevision>({
    baseDurationMinutes: service.baseDurationMinutes,
    basePriceMinor: service.basePriceMinor,
    includedAreaM2: service.includedAreaM2,
    minutesPerExtraM2: service.minutesPerExtraM2,
    pricePerExtraM2Minor: service.pricePerExtraM2Minor,
    reason: "Плановое обновление тарифа",
  });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  function numberField(key: keyof CatalogServiceRevision, value: string) {
    setRevision((current) => ({ ...current, [key]: Number(value) }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setNotice("");
    try {
      await publishServiceRevision(token, service.code, revision);
      setNotice("Новая версия опубликована и записана в аудит.");
      await onPublished();
    } catch (reason) {
      setNotice(reason instanceof Error ? reason.message : "Не удалось опубликовать");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel editorPanel">
      <div className="editorHeading">
        <div>
          <p className="cardLabel">Правила цены</p>
          <h2>{service.name}</h2>
          <p>{service.description}</p>
        </div>
        <span className="kindBadge">{service.kind}</span>
      </div>
      <form className="editorForm" onSubmit={submit}>
        <label>
          Базовая цена, тиын
          <input
            min={100000}
            onChange={(event) => numberField("basePriceMinor", event.target.value)}
            type="number"
            value={revision.basePriceMinor}
          />
        </label>
        <label>
          Включённая площадь, м²
          <input
            min={10}
            onChange={(event) => numberField("includedAreaM2", event.target.value)}
            type="number"
            value={revision.includedAreaM2}
          />
        </label>
        <label>
          Доплата за м², тиын
          <input
            min={0}
            onChange={(event) => numberField("pricePerExtraM2Minor", event.target.value)}
            type="number"
            value={revision.pricePerExtraM2Minor}
          />
        </label>
        <label>
          Базовая длительность, мин
          <input
            min={30}
            onChange={(event) => numberField("baseDurationMinutes", event.target.value)}
            type="number"
            value={revision.baseDurationMinutes}
          />
        </label>
        <label>
          Минут на дополнительный м²
          <input
            min={0}
            onChange={(event) => numberField("minutesPerExtraM2", event.target.value)}
            type="number"
            value={revision.minutesPerExtraM2}
          />
        </label>
        <label className="wideField">
          Причина изменения
          <textarea
            minLength={8}
            onChange={(event) =>
              setRevision((current) => ({ ...current, reason: event.target.value }))
            }
            required
            rows={3}
            value={revision.reason}
          />
        </label>
        {notice && (
          <p className="wideField formNotice" role="status">
            {notice}
          </p>
        )}
        <button className="primaryButton wideField" disabled={busy} type="submit">
          {busy ? "Публикуем…" : "Опубликовать новую версию"}
        </button>
      </form>
      <div className="scopeColumns">
        <div>
          <h3>Входит</h3>
          <ul>
            {service.includes.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
        <div>
          <h3>Не входит</h3>
          <ul>
            {service.excludes.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function formatMoney(minor: number): string {
  return `${new Intl.NumberFormat("ru-RU").format(Math.round(minor / 100))} ₸`;
}
